/**
 * The conversation with one client, found or opened.
 *
 * The inbox already names the client of every conversation (`recipientCustomerId`), so a client the
 * trainer has written to before is found there with no request. A client with no conversation yet is
 * opened the way the mobile app opens one: the client's e-mail from `GET /customer/{id}`, then
 * `POST /chat/conversations/direct-by-email`, which is get-or-create and so safe to repeat. The new
 * conversation is written into the inbox at once, so the surface that asked can draw it before the
 * inbox's own re-read returns.
 */

import { isCancelledError, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { useSessionUser } from '~/entities/session';
import { getCustomerId } from '~/shared/api/generated/endpoints/customer/customer';
import {
  getChatConversations,
  postChatConversationsDirectByEmail,
} from '~/shared/api/generated/endpoints/chat/chat';
import type { TrainWithMeModulesChatDtosResponsesConversationResponse as ConversationResponse } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';

import { conversationsQueryKey, conversationsQueryOptions } from '../api/chatQueries';
import { isOwnProfile, openFailureOf, type OpenFailure } from '../model/openFailure';

import { useChatSession } from './chatSessionContext';

/**
 * The inbox, from the cache or read for it. The read is SHARED with the inbox's own observer, and
 * that observer unmounting (a route change, React's double mount in development) cancels it — a
 * cancellation is not an answer, so the read is asked again rather than the open reported failed.
 */
async function knownConversations(queryClient: QueryClient): Promise<ConversationResponse[]> {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await queryClient.ensureQueryData(conversationsQueryOptions());
    } catch (error) {
      if (!isCancelledError(error)) throw error;
    }
  }
  return getChatConversations();
}

/** The conversation's id, or why there is none. */
export type OpenedConversation =
  | { readonly id: number; readonly failure: null }
  | { readonly id: null; readonly failure: OpenFailure };

export interface OpenConversation {
  readonly open: (customerId: number, email?: string | null) => Promise<OpenedConversation>;
  readonly opening: number | null;
  readonly failed: number | null;
}

export function useOpenConversation(): OpenConversation {
  const queryClient = useQueryClient();
  const session = useChatSession();
  const [opening, setOpening] = useState<number | null>(null);
  const [failed, setFailed] = useState<number | null>(null);
  const inFlight = useRef(new Map<number, Promise<OpenedConversation>>());
  const selfCustomerId = useSessionUser()?.selfCustomerId ?? null;

  const open = (customerId: number, email?: string | null): Promise<OpenedConversation> => {
    // The trainer's own profile: the backend would refuse it, so it is not asked.
    if (isOwnProfile(customerId, selfCustomerId)) return Promise.resolve({ id: null, failure: 'self' });
    const running = inFlight.current.get(customerId);
    if (running !== undefined) return running;
    const task = (async (): Promise<OpenedConversation> => {
      setOpening(customerId);
      setFailed(null);
      try {
        const known = await knownConversations(queryClient);
        const existing = known.find((row) => row.recipientCustomerId === customerId);
        if (typeof existing?.id === 'number') return { id: existing.id, failure: null };
        const address = email ?? (await getCustomerId(customerId)).email ?? null;
        if (address === null || address === '') throw new Error('The client has no e-mail to open a conversation with');
        const conversation = await postChatConversationsDirectByEmail({ email: address });
        if (typeof conversation.id !== 'number') throw new Error('The conversation came back without an id');
        const id = conversation.id;
        queryClient.setQueryData<ConversationResponse[]>(conversationsQueryKey, (rows) =>
          rows === undefined || rows.some((row) => row.id === id) ? rows : [conversation, ...rows],
        );
        session.join(id);
        return { id, failure: null };
      } catch (error) {
        setFailed(customerId);
        return { id: null, failure: openFailureOf(error instanceof ApiError ? error.code : undefined) };
      } finally {
        setOpening(null);
        inFlight.current.delete(customerId);
      }
    })();
    inFlight.current.set(customerId, task);
    return task;
  };

  return { open, opening, failed };
}
